/**
 * Migration CTA banner web component.
 * Markup/styles live in Liquid; this only handles Recharge portal auth.
 * Expects window.recharge to be initialized before this element upgrades.
 * Config (API key, express payment methods, min orders, amber cohort flag) comes from theme settings via data attributes.
 */
class MigrationCtaBanner extends HTMLElement {
  connectedCallback() {
    this.bootstrapRechargeAuth();
  }

  get apiKey() {
    return this.dataset.apiKey || '';
  }

  get expressPaymentMethods() {
    return (this.dataset.expressPaymentMethods || '')
      .split(',')
      .map((method) => method.trim())
      .filter(Boolean);
  }

  get minOrders() {
    const value = Number(this.dataset.minOrders);
    return Number.isFinite(value) ? value : 2;
  }

  get showAmberCohort() {
    return this.dataset.showAmberCohort === 'true';
  }

  async bootstrapRechargeAuth() {
    try {
      if (!window.recharge || !window.recharge.auth) {
        return;
      }

      const session = await window.recharge.auth.loginCustomerPortal();


      const [customer, ordersResponse] = await Promise.all([
        window.recharge.customer.getCustomer(session, {
          include: [
            'addresses',
            'announcements',
            'gift_purchases',
            'metafields',
            'payment_methods',
            'punch_card_progress',
            'referral_info',
            'subscriptions',
          ],
        }),
        window.recharge.order.listOrders(session, {
          limit: this.minOrders + 1,
        }),
      ]);

      const isOverMinOrders = this.isOverMinOrders(ordersResponse?.orders || []);
      const isExpressPaymentMethod = this.isExpressPaymentMethod(customer);
      const hasNoActiveSubscriptions = customer.subscriptions_active_count === 0;
      if (hasNoActiveSubscriptions == true) {
        this.onStoreMigrationCompleted(customer);
      } else if (this.showAmberCohort && isOverMinOrders && isExpressPaymentMethod) {
        this.showAmberCohortBanner();
        const magicLink = await this.createMagicLink(customer.email);

        if (magicLink) {
          this.updateBannerCtaLink(magicLink);
        }
      }
    } catch (error) {
      console.error('[migration-cta] Recharge auth failed', error);
    }
  }

  isOverMinOrders(orders) {
    return orders.length > this.minOrders;
  }

  showAmberCohortBanner() {
    const pending = this.querySelector('[data-migration-state="pending"]');
    if (pending) pending.hidden = false;
  }

  /**
 * Green cohort: has a migration.payment_method that is not an express method.
 */
  isGreenCohort(customer) {

    const metafields = customer?.include?.metafields || [];
    const methods = this.expressPaymentMethods;

    return metafields.some(
      (field) =>
        field.namespace === 'migration' &&
        field.key === 'payment_method' &&
        field.value &&
        !methods.includes(field.value)
    );
  }


  updateBannerCtaLink(magicLink) {
    const ctaLink = this.querySelector('[data-migration-cta-link]');
    if (ctaLink) ctaLink.href = magicLink;
  }

  isExpressPaymentMethod(customer) {
    const metafields = customer?.include?.metafields || [];
    const methods = this.expressPaymentMethods;

    return metafields.some(
      (field) =>
        field.namespace === 'migration' &&
        field.key === 'payment_method' &&
        methods.includes(field.value)
    );
  }

  async createMagicLink(customerEmail) {
    const response = await fetch(
      'https://recharge-migration-script-production.up.railway.app/customers/lookup',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': this.apiKey,
        },
        body: JSON.stringify({ email: String(customerEmail) }),
      }
    );

    if (!response.ok) {
      throw new Error(`[migration-cta] Magic link lookup failed (${response.status})`);
    }

    const data = await response.json();

    return data.portal_url;
  }

  /**
   * True when customer has store_migration.status = "completed".
   */
  isStoreMigrationCompleted(customer) {
    const metafields = customer?.include?.metafields || [];
    return metafields.some(
      (field) =>
        field.namespace === 'store_migration' &&
        field.key === 'status' &&
        field.value === 'completed'
    );
  }

  /**
   * Swap pending CTA for the completed-state CTA.
   */
  onStoreMigrationCompleted() {
    const pending = this.querySelector('[data-migration-state="pending"]');
    const completed = this.querySelector('[data-migration-state="completed"]');

    if (pending) pending.hidden = true;
    if (completed) completed.hidden = false;

    this.hideRechargeCustomerPortal();
  }

  /**
   * Hide #recharge-customer-portal once Recharge injects it into the DOM.
   */
  hideRechargeCustomerPortal() {
    const hide = (el) => {
      if (!el) return false;
      el.hidden = true;
      el.style.setProperty('display', 'none', 'important');
      return true;
    };

    if (hide(document.querySelector('#recharge-customer-portal'))) return;

    const observer = new MutationObserver(() => {
      if (hide(document.querySelector('#recharge-customer-portal'))) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    this._rechargePortalObserver = observer;
  }

  disconnectedCallback() {
    this._rechargePortalObserver?.disconnect();
  }
}

if (!customElements.get('migration-cta-banner')) {
  customElements.define('migration-cta-banner', MigrationCtaBanner);
}
